import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { Button } from '@/lib/base/buttons/button';
import type { OnboardingStepDetail } from '@/lib/onboarding/service';
import { onboardingService } from '@/lib/onboarding/service';

interface OnboardingRetryNoticeProps {
  orgId: number;
  steps: OnboardingStepDetail[];
}

/**
 * Non-blocking notice shown in the actions panel when a best-effort onboarding
 * step failed in a way the user can fix (e.g. missing Gmail/Calendar scopes).
 *
 * After the optional-step change the pipeline completes regardless, so the user
 * reaches a usable app — but a scope failure would otherwise leave them with an
 * empty, unexplained inbox. This surfaces a retry so they can finish importing.
 * Renders nothing when there are no retryable failures.
 */
export function OnboardingRetryNotice({
  orgId,
  steps,
}: OnboardingRetryNoticeProps) {
  const queryClient = useQueryClient();

  const retryableSteps = useMemo(
    () => steps.filter(step => step.status === 'failed' && step.can_retry),
    [steps]
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      Promise.allSettled(
        retryableSteps.map(step =>
          onboardingService.retryStep(orgId, step.step_name)
        )
      ),
    onSuccess: (results: PromiseSettledResult<unknown>[]) => {
      queryClient.invalidateQueries({ queryKey: ['onboarding-status', orgId] });
      const failedCount = results.filter(r => r.status === 'rejected').length;
      if (failedCount === 0) {
        toast.success('Retrying your setup…');
      } else if (failedCount < results.length) {
        toast.success('Some steps are retrying – others could not be started.');
      } else {
        toast.error("We couldn't retry that. Please try again in a moment.");
      }
    },
  });

  const handleRetry = useCallback(() => mutate(), [mutate]);

  if (retryableSteps.length === 0) {
    return null;
  }

  const labels = retryableSteps.map(step => step.display_name).join(', ');

  return (
    <div
      data-component='onboarding-retry-notice'
      className='mx-[16px] my-[12px] flex items-start gap-3 rounded-[8px] border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] p-3'
    >
      <AlertTriangle
        data-icon
        className='mt-[2px] size-5 shrink-0 text-[var(--color-text-warning-primary)]'
      />
      <div className='flex min-w-0 flex-1 flex-col gap-2'>
        <div className='flex flex-col gap-1'>
          <p className='text-body-small font-medium text-[var(--color-text-primary)]'>
            We couldn’t finish setting up {labels}
          </p>
          <p className='text-body-small text-[var(--color-text-secondary)]'>
            Reconnect your account or try again to finish importing your data.
          </p>
        </div>
        <div>
          <Button
            size='sm'
            color='secondary'
            onClick={handleRetry}
            isDisabled={isPending}
          >
            {isPending ? 'Retrying…' : 'Try again'}
          </Button>
        </div>
      </div>
    </div>
  );
}
